import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, Layers } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { services as mockServices } from '../data/mockData';
import { Service } from '../types';
export function ServicesManagement() {
  const [services, setServices] = useState<Service[]>(mockServices);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [formData, setFormData] = useState<Partial<Service>>({
    name: '',
    name_en: '',
    has_billing: false,
    row_status: 'active'
  });
  const filteredServices = services.filter(service => service.name.toLowerCase().includes(searchTerm.toLowerCase()) || service.name_en.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleCreateOpen = () => {
    setEditingService(null);
    setFormData({
      name: '',
      name_en: '',
      has_billing: false,
      row_status: 'active'
    });
    setIsModalOpen(true);
  };
  const handleEditOpen = (service: Service) => {
    setEditingService(service);
    setFormData(service);
    setIsModalOpen(true);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      setServices(services.filter(s => s.id !== id));
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingService) {
      setServices(services.map(s => s.id === editingService.id ? {
        ...s,
        ...formData
      } as Service : s));
    } else {
      const newService: Service = {
        ...(formData as Service),
        id: `s${Date.now()}`,
        uuid: `uuid-${Date.now()}` // Mock server-side generation
      };
      setServices([...services, newService]);
    }
    setIsModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-478">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-479">
        <div data-id="element-480">
          <h1 className="text-2xl font-bold text-slate-800" data-id="element-481">
            Services Management
          </h1>
          <p className="text-slate-500" data-id="element-482">
            Manage system services and configurations
          </p>
        </div>
        <Button onClick={handleCreateOpen} data-id="element-483">
          <Plus className="mr-2 h-4 w-4" data-id="element-484" /> Add New Service
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200" data-id="element-485">
        <div className="relative max-w-md" data-id="element-486">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-487" />
          <input type="text" placeholder="Search services..." className="w-full h-10 pl-9 pr-4 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-488" />
        </div>
      </div>

      {/* Services Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden" data-id="element-489">
        <div className="overflow-x-auto" data-id="element-490">
          <table className="w-full text-sm text-left" data-id="element-491">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-492">
              <tr data-id="element-493">
                <th className="px-6 py-3" data-id="element-494">Service Name (EN)</th>
                <th className="px-6 py-3" data-id="element-495">Service Name (BN)</th>
                <th className="px-6 py-3" data-id="element-496">UUID</th>
                <th className="px-6 py-3" data-id="element-497">Billing</th>
                <th className="px-6 py-3" data-id="element-498">Status</th>
                <th className="px-6 py-3 text-right" data-id="element-499">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-500">
              {filteredServices.map(service => <tr key={service.id} className="hover:bg-slate-50 transition-colors" data-id="element-501">
                  <td className="px-6 py-4 font-medium text-slate-900" data-id="element-502">
                    {service.name_en}
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-503">{service.name}</td>
                  <td className="px-6 py-4 text-xs font-mono text-slate-500" data-id="element-504">
                    {service.uuid}
                  </td>
                  <td className="px-6 py-4" data-id="element-505">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${service.has_billing ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}`} data-id="element-506">
                      {service.has_billing ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-507">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${service.row_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-508">
                      {service.row_status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-509">
                    <div className="flex items-center justify-end space-x-2" data-id="element-510">
                      <button onClick={() => handleEditOpen(service)} className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors" data-id="element-511">
                        <Edit2 size={16} data-id="element-512" />
                      </button>
                      <button onClick={() => handleDelete(service.id)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-id="element-513">
                        <Trash2 size={16} data-id="element-514" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingService ? 'Edit Service' : 'Add New Service'} data-id="element-515">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-516">
          <Input label="Service Name (English)" value={formData.name_en} onChange={e => setFormData({
          ...formData,
          name_en: e.target.value
        })} required data-id="element-517" />
          <Input label="Service Name (Bengali)" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} required data-id="element-518" />

          <div className="flex items-center space-x-2 pt-2" data-id="element-519">
            <input type="checkbox" id="has_billing" className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4" checked={formData.has_billing} onChange={e => setFormData({
            ...formData,
            has_billing: e.target.checked
          })} data-id="element-520" />
            <label htmlFor="has_billing" className="text-sm font-medium text-slate-700" data-id="element-521">
              Enable Billing Feature
            </label>
          </div>

          <Select label="Status" options={[{
          value: 'active',
          label: 'Active'
        }, {
          value: 'inactive',
          label: 'Inactive'
        }]} value={formData.row_status} onChange={e => setFormData({
          ...formData,
          row_status: e.target.value as 'active' | 'inactive'
        })} data-id="element-522" />

          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 mt-4" data-id="element-523">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-524">
              Cancel
            </Button>
            <Button type="submit" data-id="element-525">Save Service</Button>
          </div>
        </form>
      </Modal>
    </div>;
}